import React, {
  FC,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, AlertProps, Avatar, Badge, Button, Drawer, List } from 'antd';
import { Stack } from './Stack';
import {
  colorBorderSecondary,
  colorGray,
  colorPrimary,
} from '@atlas-frontend/design-tokens';
import styled from 'styled-components';
import * as Icon from 'react-feather';
import {
  ValidationMessage,
  ValidationMessagesMap,
} from '../utils/ValidationMessagesMap';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ErrorAlertWithDrawer.translations.json';
import { useUserSelectBlockers } from '../UserSelectBlocker';
import { createPortal } from 'react-dom';

const { addTranslations, useTranslation } = i18nNsFactory(
  'error-alert-with-drawer',
);
addTranslations(translations);

const SLICE_SIZE = 10;
const DRAWER_MIN_WIDTH = 360;

const getDrawerMaxWidth = () => Math.floor(window.innerWidth * 0.8);
const clampDrawerWidth = (width: number) =>
  Math.max(DRAWER_MIN_WIDTH, Math.min(getDrawerMaxWidth(), width));

export interface ErrorAlertWithDrawerProps extends AlertProps {
  validationMessagesMap: ValidationMessagesMap;
  renderErrorHeading?: (
    name: string,
    messages: ValidationMessage[],
  ) => ReactNode;
  renderErrorMeta?: (
    name: string,
    message: ValidationMessage[],
    index: number,
  ) => ReactNode;
  isErrorDrawerOpen?: boolean;
  onClickDrawerClose?: (event: MouseEvent | KeyboardEvent) => void;
  onClickOpenDrawer?: (event: MouseEvent) => void;
  onDrawerOpenChange?: (open: boolean) => void;
  errorAction?: string;
  drawerFooter?: ReactNode;
  errorPrewrap?: boolean;
}

export const ErrorAlertWithDrawer: FC<ErrorAlertWithDrawerProps> = (props) => {
  const [isErrorDrawerOpen, setIsErrorDrawerOpen] = useState(false);
  if (typeof props.isErrorDrawerOpen === 'undefined') {
    return (
      <ErrorAlertWithDrawerUncontrolled
        {...props}
        isErrorDrawerOpen={isErrorDrawerOpen}
        onClickDrawerClose={() => setIsErrorDrawerOpen(false)}
        onClickOpenDrawer={() => setIsErrorDrawerOpen(true)}
      />
    );
  }

  return (
    <ErrorAlertWithDrawerUncontrolled
      {...props}
      isErrorDrawerOpen={props.isErrorDrawerOpen}
      onClickDrawerClose={props.onClickDrawerClose}
      onClickOpenDrawer={props.onClickOpenDrawer}
    />
  );
};

export const ErrorAlertWithDrawerUncontrolled: FC<ErrorAlertWithDrawerProps> = (
  props,
) => {
  const {
    validationMessagesMap,
    renderErrorHeading,
    renderErrorMeta,
    isErrorDrawerOpen = false,
    onClickDrawerClose,
    onClickOpenDrawer,
    onDrawerOpenChange,
    drawerFooter,
    errorPrewrap,
    ...alertProps
  } = props;
  const { t } = useTranslation();
  const [sliceAt, setSliceAt] = useState(SLICE_SIZE);
  const [drawerWidth, setDrawerWidth] = useState(() =>
    clampDrawerWidth(DRAWER_MIN_WIDTH),
  );
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef<{ x: number; width: number } | null>(null);
  const { add: addUserSelectBlocker } = useUserSelectBlockers();

  const onMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsResizing(true);
      resizeStartRef.current = {
        x: event.clientX,
        width: drawerWidth,
      };
    },
    [drawerWidth],
  );

  const onMouseUp = useCallback(() => {
    setIsResizing(false);
    resizeStartRef.current = null;
  }, []);

  const onMouseMove = useCallback(
    (event: globalThis.MouseEvent) => {
      const start = resizeStartRef.current;
      if (!isResizing || !start) return;

      const delta = start.x - event.clientX;
      setDrawerWidth(clampDrawerWidth(start.width + delta));
    },
    [isResizing],
  );

  useEffect(() => {
    if (isResizing) {
      return addUserSelectBlocker();
    }
  }, [isResizing, addUserSelectBlocker]);

  useEffect(() => {
    const onResize = () => {
      setDrawerWidth((current) => clampDrawerWidth(current));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const messages = useMemo(
    () => Array.from(validationMessagesMap.entries()),
    [validationMessagesMap],
  );

  const dataSource = useMemo(
    () => messages.slice(0, sliceAt),
    [messages, sliceAt],
  );

  const isSliced = dataSource.length < messages.length;
  const isSliceable = messages.length > SLICE_SIZE;

  const showMore = useCallback(() => {
    setSliceAt((current) => current + SLICE_SIZE);
  }, []);
  const showLess = useCallback(() => {
    setSliceAt((current) => current - SLICE_SIZE);
  }, []);

  const { totalErrors, totalWarnings } = validationMessagesMap;
  const shouldRenderResizeTrigger =
    isErrorDrawerOpen && typeof document !== 'undefined';

  return (
    <>
      {!!messages.length && (
        <ErrorAlert
          totalErrors={totalErrors}
          totalWarnings={totalWarnings}
          onClickOpenDrawer={onClickOpenDrawer}
          {...alertProps}
        />
      )}
      {shouldRenderResizeTrigger &&
        createPortal(
          <DrawerResizeTrigger
            $rightOffset={drawerWidth}
            onMouseDown={onMouseDown}
          />,
          document.body,
        )}
      <Drawer
        title={<ErrorDrawerHeading>{t('drawer.title')}</ErrorDrawerHeading>}
        placement="right"
        onClose={onClickDrawerClose}
        open={isErrorDrawerOpen}
        afterOpenChange={onDrawerOpenChange}
        footer={drawerFooter}
        width={drawerWidth}
        styles={{
          body: {
            position: 'relative',
          },
          wrapper: {
            transition: isResizing ? 'none' : undefined,
          },
        }}
      >
        <List
          itemLayout="vertical"
          dataSource={dataSource}
          renderItem={([name, messages], index) => {
            const errors = messages.filter((item) => item.type === 'error');
            const warnings = messages.filter((item) => item.type === 'warning');
            const hasErrors = errors.length > 0;
            const hasWarnings = warnings.length > 0;
            return (
              <List.Item
                style={
                  errorPrewrap
                    ? {
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                      }
                    : {}
                }
              >
                {hasErrors && (
                  <ErrorItemMeta
                    style={
                      hasWarnings
                        ? {
                            marginBlockEnd: 0,
                          }
                        : {}
                    }
                    avatar={
                      <ErrorAvatar
                        style={
                          hasWarnings
                            ? {
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                              }
                            : {}
                        }
                        shape="square"
                      />
                    }
                    description={
                      <ErrorDescription
                        renderErrorHeading={renderErrorHeading}
                        renderErrorMeta={(name, message) =>
                          renderErrorMeta?.(name, message, index)
                        }
                        messages={errors}
                        name={name}
                      />
                    }
                  />
                )}
                {hasWarnings && (
                  <ErrorItemMeta
                    avatar={
                      <WarningAvatar
                        style={
                          hasErrors
                            ? {
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                              }
                            : {}
                        }
                        shape="square"
                      />
                    }
                    description={
                      <ErrorDescription
                        renderErrorHeading={renderErrorHeading}
                        renderErrorMeta={(name, message) =>
                          renderErrorMeta?.(name, message, index)
                        }
                        messages={warnings}
                        name={name}
                      />
                    }
                  />
                )}
              </List.Item>
            );
          }}
          footer={
            <Stack direction="horizontal" size="xs">
              {isSliced && (
                <Button id="show-more-validation-violations" onClick={showMore}>
                  <Stack direction="horizontal" size="xs" align="center">
                    {t('drawer.list.footer.button.show-more')}
                    <Badge
                      count={messages.length - sliceAt}
                      overflowCount={Infinity}
                      color={colorGray[2]}
                      styles={{
                        indicator: {
                          color: colorGray[7],
                        },
                      }}
                    />
                  </Stack>
                </Button>
              )}
              {isSliceable && sliceAt > SLICE_SIZE && (
                <Button id="show-less-validation-violations" onClick={showLess}>
                  {t('drawer.list.footer.button.show-less')}
                </Button>
              )}
            </Stack>
          }
        />
      </Drawer>
    </>
  );
};

interface ErrorAlertProps extends AlertProps {
  errorAction?: string;
  totalErrors: number;
  totalWarnings: number;
  onClickOpenDrawer?: (event: MouseEvent) => void;
}

const ErrorAlert: FC<ErrorAlertProps> = (props) => {
  const {
    totalErrors,
    totalWarnings,
    onClickOpenDrawer,
    action,
    errorAction,
    ...rest
  } = props;
  const { t } = useTranslation();

  const alertType = useMemo(
    () => (totalErrors ? 'error' : 'warning'),
    [totalErrors],
  );

  const alertMessage = useMemo(() => {
    const messageAction = errorAction ?? t('action.saving');
    if (totalErrors) {
      return totalWarnings
        ? t('message.errorWithWarning', {
            totalErrors,
            totalWarnings,
            action: messageAction,
          })
        : t('message.error', { totalErrors, action: messageAction });
    }
    return t('message.warning');
  }, [errorAction, totalErrors, t, totalWarnings]);

  return (
    <Alert
      type={alertType}
      icon={alertType === 'error' ? <Icon.AlertTriangle /> : undefined}
      showIcon={alertType === 'error'}
      message={alertMessage}
      action={
        <Stack direction="horizontal" size="xs">
          <Button id="open-errors-drawer" onClick={onClickOpenDrawer}>
            {t('button.view')}
          </Button>
          {action}
        </Stack>
      }
      {...rest}
      banner
    />
  );
};

interface ErrorDescriptionProps {
  renderErrorHeading?: (
    name: string,
    messages: ValidationMessage[],
  ) => ReactNode;
  renderErrorMeta?: (name: string, message: ValidationMessage[]) => ReactNode;
  messages: ValidationMessage[];
  name: string;
}

const ErrorDescription: FC<ErrorDescriptionProps> = (props) => {
  const { renderErrorHeading, renderErrorMeta, messages, name } = props;

  return (
    <Stack size="xs">
      <div>
        {renderErrorHeading && (
          <ErrorHeading>{renderErrorHeading(name, messages)}</ErrorHeading>
        )}
        <MessagesWrapper size="xs">
          {messages.map(({ message }, index) => (
            <Stack key={index} size="xs">
              <div>{message}</div>
            </Stack>
          ))}
        </MessagesWrapper>
      </div>
      {renderErrorMeta?.(name, messages)}
    </Stack>
  );
};

const ErrorDrawerHeading = styled.h3`
  margin: 0;
`;

const ErrorItemMeta = styled(List.Item.Meta)`
  .ant-list-item-meta-avatar {
    align-self: stretch;
  }
`;

const ErrorAvatar = styled(Avatar)`
  background-color: #ff8d43;
  height: 100%;
  width: 10px;
`;

const WarningAvatar = styled(Avatar)`
  background-color: #ffe58f;
  height: 100%;
  width: 10px;
`;

const ErrorHeading = styled.h5`
  margin: 0;
`;

const MessagesWrapper = styled(Stack)`
  > div + div:before {
    content: '';
    display: block;
    height: 1px;
    background-color: ${colorBorderSecondary};
  }
`;

const DrawerResizeTrigger = styled.div<{ $rightOffset: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  width: 8px;
  right: ${({ $rightOffset }) => `${$rightOffset - 5}px`};
  z-index: 1100;
  cursor: ew-resize;

  &:after {
    content: ' ';
    position: absolute;
    top: 0;
    left: 3px;
    background-color: transparent;
    transition: background-color 0.2s;
    width: 2px;
    height: 100%;
  }

  &:hover:after {
    background-color: ${colorPrimary};
  }
`;
